/**
 * Where the "what next" pointer sits on screen. On screen it hangs over the target; off screen (or behind
 * the camera) it is pinned to the edge of a safe frame — inset so it never lands on the minimap, the
 * system knobs or the thumbs — on the line from the screen centre towards the target, pointing at it.
 * Pure; unit-tested in onboarding.test.ts.
 */

export interface Frame {
  /** Insets of the safe frame, in px. */
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface PointerPlace {
  x: number;
  y: number;
  /** Radians, screen space (0 = right, +y down): which way the arrow points. */
  angle: number;
  onScreen: boolean;
}

/**
 * @param sx,sy projected screen position (px) of the target
 * @param behind the target is behind the camera (its projection is mirrored)
 */
export function edgePointer(sx: number, sy: number, behind: boolean, w: number, h: number, f: Frame): PointerPlace {
  const x0 = f.left;
  const x1 = w - f.right;
  const y0 = f.top;
  const y1 = h - f.bottom;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  let dx = sx - cx;
  let dy = sy - cy;
  if (behind) {
    dx = -dx;
    dy = -dy;
    // Straight behind: point down the screen (towards the camera side).
    if (Math.abs(dx) < 1e-3 && Math.abs(dy) < 1e-3) dy = 1;
  }
  const inside = !behind && sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1;
  if (inside) return { x: sx, y: sy, angle: Math.PI / 2, onScreen: true };
  const hx = (x1 - x0) / 2;
  const hy = (y1 - y0) / 2;
  const tx = Math.abs(dx) > 1e-6 ? hx / Math.abs(dx) : Infinity;
  const ty = Math.abs(dy) > 1e-6 ? hy / Math.abs(dy) : Infinity;
  const t = Math.min(tx, ty);
  return { x: cx + dx * t, y: cy + dy * t, angle: Math.atan2(dy, dx), onScreen: false };
}
